// Base local IndexedDB (§9, §13). Un solo esquema versionado para borradores y cola de adjuntos.
// El Blob y sus metadatos viven en el MISMO registro: se escriben y borran juntos.
// La persistencia local es una ayuda, no una garantía (modo privado, borrado del navegador).
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export const DB_NAME = 'servicioos';
export const DB_VERSION = 1;

export interface DraftRecord {
  key: string; // `${userId}:${orderId}`
  userId: string;
  orderId: string;
  baseVersion: number;
  /** Valores guardados en el servidor cuando se empezó el borrador (detecta cambios ajenos). */
  base: { model: string; serial: string; diagnosis: string; solution: string };
  /** Ausente en borradores anteriores al campo Modelo (getDraft lo completa con ''). */
  model: string;
  serial: string;
  diagnosis: string;
  solution: string;
  updatedAt: number;
}

/**
 * ready: comprimido y guardado, sin reserva · reserved: reserva creada en servidor
 * failed: último intento falló (permanent indica que no se reintentará)
 * confirmed: ready en servidor pero la limpieza local falló; se purga después
 */
export type QueuePhase = 'ready' | 'reserved' | 'failed' | 'confirmed';

export interface QueueRecord {
  id: string; // UUID estable del archivo (media_id)
  userId: string;
  orderId: string;
  /** OS (registros antiguos pueden traer un número). */
  folio: string;
  kind: 'image' | 'video';
  name: string;
  blob: Blob;
  bytes: number;
  duration: number | null;
  phase: QueuePhase;
  error: string | null;
  permanent: boolean;
  attempts: number;
  createdAt: number;
  updatedAt: number;
}

interface ServicioDB extends DBSchema {
  drafts: { key: string; value: DraftRecord; indexes: { byUser: string } };
  queue: { key: string; value: QueueRecord; indexes: { byUser: string; byUserOrder: [string, string] } };
}

export type LocalDb = IDBPDatabase<ServicioDB>;

let dbPromise: Promise<LocalDb> | null = null;

export function getDb(): Promise<LocalDb> {
  dbPromise ??= openDB<ServicioDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // Migraciones acumulativas: cada versión nueva agrega un bloque sin borrar pendientes.
      if (oldVersion < 1) {
        const drafts = db.createObjectStore('drafts', { keyPath: 'key' });
        drafts.createIndex('byUser', 'userId');
        const queue = db.createObjectStore('queue', { keyPath: 'id' });
        queue.createIndex('byUser', 'userId');
        queue.createIndex('byUserOrder', ['userId', 'orderId']);
      }
    },
    blocked() {
      // Otra pestaña con una versión anterior abierta: se reintentará al recargar.
    },
  }).catch((error: unknown) => {
    dbPromise = null;
    throw error;
  });
  return dbPromise;
}

/** Solo para pruebas: cierra y olvida la conexión. */
export async function resetDbForTests(): Promise<void> {
  if (dbPromise) (await dbPromise).close();
  dbPromise = null;
}
